import {
  buildPaymentReturnUrl,
  isUnresolvedCheckoutSessionId as isUnresolvedPaymentReturnCheckoutSessionId,
  STRIPE_CHECKOUT_SESSION_ID_TEMPLATE as PAYMENT_RETURN_CHECKOUT_SESSION_ID_TEMPLATE,
} from "@/pages/PaymentReturn/paymentReturnFlow";

export const CREDIT_TOPUP_RETURN_PARAMS = {
  paymentModal: "payment_modal",
  result: "topup_result",
  checked: "topup_checked",
  sessionId: "session_id",
  workspaceId: "workspace_id",
} as const;

export const CREDIT_TOPUP_PENDING_SESSION_STORAGE_KEY =
  "bustly_credit_topup_checkout_session_id";

export const STRIPE_CHECKOUT_SESSION_ID_TEMPLATE =
  PAYMENT_RETURN_CHECKOUT_SESSION_ID_TEMPLATE;

export type CreditTopupReturnResult = "success" | "cancel";

interface BuildCreditTopupReturnUrlsParams {
  origin?: string;
  workspaceId?: string | null;
}

export interface CreditTopupReturnState {
  result: CreditTopupReturnResult | null;
  checked: boolean;
  sessionId: string | null;
  workspaceId: string | null;
}

const normalizeReturnResult = (
  value: string | null,
): CreditTopupReturnResult | null => {
  if (value === "success" || value === "cancel") {
    return value;
  }

  return null;
};

export const isUnresolvedCheckoutSessionId = (
  value?: string | null,
): boolean => isUnresolvedPaymentReturnCheckoutSessionId(value);

export const writePendingCreditTopupCheckoutSessionId = (
  sessionId?: string | null,
) => {
  const normalizedSessionId = sessionId?.trim();
  if (
    typeof window === "undefined" ||
    !normalizedSessionId ||
    isUnresolvedCheckoutSessionId(normalizedSessionId)
  ) {
    return;
  }

  window.sessionStorage.setItem(
    CREDIT_TOPUP_PENDING_SESSION_STORAGE_KEY,
    normalizedSessionId,
  );
};

export const readPendingCreditTopupCheckoutSessionId = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.sessionStorage.getItem(CREDIT_TOPUP_PENDING_SESSION_STORAGE_KEY);
};

export const clearPendingCreditTopupCheckoutSessionId = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(CREDIT_TOPUP_PENDING_SESSION_STORAGE_KEY);
};

export const buildCreditTopupReturnUrls = ({
  origin,
  workspaceId,
}: BuildCreditTopupReturnUrlsParams) => {
  const resolvedOrigin =
    origin ||
    (typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "") ||
    "https://salerio.com";
  return {
    successUrl: buildPaymentReturnUrl({
      origin: resolvedOrigin,
      kind: "credit_topup",
      result: "success",
      returnPath: "/usage",
      workspaceId,
      includeCheckoutSessionId: true,
    }),
    cancelUrl: buildPaymentReturnUrl({
      origin: resolvedOrigin,
      kind: "credit_topup",
      result: "cancel",
      returnPath: "/usage",
      workspaceId,
    }),
  };
};

export const readCreditTopupReturnState = (
  searchParams: URLSearchParams,
  fallbackSessionId?: string | null,
): CreditTopupReturnState => {
  const sessionId = searchParams.get(CREDIT_TOPUP_RETURN_PARAMS.sessionId);

  return {
    result: normalizeReturnResult(
      searchParams.get(CREDIT_TOPUP_RETURN_PARAMS.result),
    ),
    checked: searchParams.get(CREDIT_TOPUP_RETURN_PARAMS.checked) === "true",
    sessionId: isUnresolvedCheckoutSessionId(sessionId)
      ? fallbackSessionId || null
      : sessionId,
    workspaceId: searchParams.get(CREDIT_TOPUP_RETURN_PARAMS.workspaceId),
  };
};

export const buildCreditTopupReturnClearedUrl = (href: string): string => {
  const nextUrl = new URL(href);
  nextUrl.searchParams.delete(CREDIT_TOPUP_RETURN_PARAMS.paymentModal);
  nextUrl.searchParams.delete(CREDIT_TOPUP_RETURN_PARAMS.result);
  nextUrl.searchParams.delete(CREDIT_TOPUP_RETURN_PARAMS.checked);
  nextUrl.searchParams.delete(CREDIT_TOPUP_RETURN_PARAMS.sessionId);
  nextUrl.searchParams.delete(CREDIT_TOPUP_RETURN_PARAMS.workspaceId);

  const nextSearch = nextUrl.searchParams.toString();
  return `${nextUrl.pathname}${nextSearch ? `?${nextSearch}` : ""}${nextUrl.hash}`;
};
